import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CircleUserRound, Globe, LogOut, Moon, Sun } from 'lucide-react';

import { useAuth } from '@sshop/auth';
import { useTranslation } from '@sshop/i18n';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sshop/ui';

import { useTheme } from '@/shared/hooks/useTheme';
import { routes } from '@/shared/lib/routes';

import { useSwitchLanguage } from './Header/LanguageSwitcher';
import { accountMenuStyles as s } from './AccountMenu.styles';

export function AccountMenu() {
  const { t } = useTranslation();
  const { status, session, provider } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const { current: locale, next: nextLocale, switchTo } = useSwitchLanguage();
  const [busy, setBusy] = useState(false);
  const isAuthenticated = status === 'authenticated' && session !== null;
  const isDark = resolvedTheme === 'dark';

  const logout = async () => {
    setBusy(true);
    try {
      await provider.logout();
    } finally {
      setBusy(false);
    }
  };

  if (status === 'unknown') {
    return <span className={s.loading} aria-label={t('app.loading')} />;
  }

  return (
    <div className={s.root}>
      <div className={s.mobileOnly}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger className={s.mobileTrigger} aria-label={t('account.menu')}>
            <CircleUserRound className={s.icon} aria-hidden />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="bottom"
            sideOffset={8}
            collisionPadding={12}
            className={s.menu}
          >
            <DropdownMenuLabel className={s.menuHeading}>
              {isAuthenticated ? session.displayName : t('account.menu')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className={s.separator} />
            <DropdownMenuItem asChild className={s.primaryItem}>
              <Link to={isAuthenticated ? routes.account(locale) : routes.login(locale)}>
                <CircleUserRound className={s.icon} aria-hidden />
                <span className={s.itemText}>
                  {isAuthenticated ? t('accountDetails') : t('auth.login')}
                </span>
                <ChevronRight className={s.chevron} aria-hidden />
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator className={s.separator} />
            <DropdownMenuLabel className={s.sectionLabel}>
              {t('account.preferences')}
            </DropdownMenuLabel>
            <DropdownMenuItem
              className={s.preferenceItem}
              onSelect={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? (
                <Sun className={s.icon} aria-hidden />
              ) : (
                <Moon className={s.icon} aria-hidden />
              )}
              <span className={s.itemText}>{t('theme.label')}</span>
              <span className={s.preferenceValue}>{t(isDark ? 'theme.light' : 'theme.dark')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className={s.preferenceItem} onSelect={switchTo}>
              <Globe className={s.icon} aria-hidden />
              <span className={s.itemText}>{t('language.label')}</span>
              <span className={s.preferenceValue}>
                {t(nextLocale === 'en' ? 'language.english' : 'language.georgian')}
              </span>
            </DropdownMenuItem>
            {isAuthenticated && (
              <>
                <DropdownMenuSeparator className={s.separator} />
                <DropdownMenuItem
                  className={s.signOutItem}
                  disabled={busy}
                  onSelect={() => void logout()}
                >
                  <LogOut className={s.icon} aria-hidden />
                  <span className={s.itemText}>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isAuthenticated ? (
        <div className={s.desktopAccount}>
          <Link
            to={routes.account(locale)}
            className={s.desktopLink}
            aria-label={session.displayName}
          >
            <CircleUserRound className={s.icon} aria-hidden />
            <span className={s.displayName}>{session.displayName}</span>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className={s.desktopLogout}
            disabled={busy}
            onClick={() => void logout()}
          >
            <LogOut className={s.desktopLogoutIcon} aria-hidden />
            <span className={s.desktopLogoutLabel}>{t('auth.logout')}</span>
          </Button>
        </div>
      ) : (
        <Link to={routes.login(locale)} className={s.guestLink} aria-label={t('auth.login')}>
          <CircleUserRound className={s.icon} aria-hidden />
          <span>{t('auth.login')}</span>
        </Link>
      )}
    </div>
  );
}
